/**
 * The three things worth reporting, and what each is called at each provider.
 *
 * A pixel that only ever reports "somebody loaded a page" is a pixel that
 * cannot tell you whether the advertising works, which is the entire reason for
 * having it. These are the moments where a visitor became something: a booked
 * consultation, a completed assessment, an address on the list.
 *
 * Two rules hold for everything here.
 *
 * No personal data leaves the page. Meta supports "advanced matching", where a
 * hashed email is sent alongside the event to improve attribution, and it is
 * deliberately not used: nobody was asked about it, the Privacy Policy does not
 * describe it, and a hashed identifier is still personal data. The events say
 * that something happened, never who it happened to.
 *
 * Nothing fires without consent, and the mechanism is simply that the tags do
 * not exist until consent is given — GoogleAnalytics, Hotjar and MetaPixel each
 * render nothing until their purpose is agreed to, so `window.fbq` and
 * `window.hj` are undefined for a visitor who refused. The optional calls below
 * are therefore the consent check as well as ordinary defensiveness, which is
 * worth knowing before somebody "tidies" them into non-optional ones.
 */

export const CONVERSIONS = {
  /** A consultation slot was reserved and confirmed. */
  booking: { meta: 'Lead', hotjar: 'booking_confirmed' },
  /** The business assessment was finished and an address given for the result. */
  assessment: { meta: 'CompleteRegistration', hotjar: 'assessment_completed' },
  /** Somebody subscribed by email, from an article or the footer. */
  subscribe: { meta: 'Subscribe', hotjar: 'subscribed' },
} as const;

export type Conversion = keyof typeof CONVERSIONS;

export interface TagWindow {
  fbq?: (...args: unknown[]) => void;
  hj?: (...args: unknown[]) => void;
}

/**
 * What would be sent, given which tags are loaded. Separated from sending it so
 * the mapping can be tested without a browser, and so a reader can see at a
 * glance that no argument carries anything about a person.
 */
export function conversionCalls(
  conversion: Conversion,
  tags: { meta: boolean; hotjar: boolean }
): unknown[][] {
  const names = CONVERSIONS[conversion];
  const calls: unknown[][] = [];
  if (tags.meta) calls.push(['track', names.meta]);
  if (tags.hotjar) calls.push(['event', names.hotjar]);
  return calls;
}

/**
 * Reports a conversion to whichever tags the visitor agreed to load.
 *
 * Never throws. A tracking call that breaks a booking confirmation would be a
 * far worse failure than a missing statistic, and this runs immediately after
 * the moment it is describing.
 */
export function reportConversion(
  conversion: Conversion,
  target: TagWindow | undefined = undefined
) {
  const w = target ?? (typeof window === 'undefined' ? undefined : (window as TagWindow));
  if (!w) return;
  const names = CONVERSIONS[conversion];
  try {
    w.fbq?.('track', names.meta);
  } catch {
    /* The pixel is not ours and may fail in ways we cannot predict. */
  }
  try {
    w.hj?.('event', names.hotjar);
  } catch {
    /* Same for Hotjar. */
  }
}
